import {customElement, property} from "lit/decorators.js";
import {html, TemplateResult} from "lit";
import {OnlineReservationConfig} from "../../@types/online-reservation.js";
import {OpenOnlineReservationEvent} from "../../@types/dom.js";
import {OnlineReservationBase} from './online-reservation-base.js';
import {EntsDrawer} from "../drawer/drawer.js";
import { track } from '../tracking/generic-tracking-layer.js';
import { openNativeDialog } from "@mobile-app/native-dialog";

/**
 * Web/Drawer implementation of Online Reservation.
 * Handles opening in a drawer for web browsers or triggering native dialog for mobile apps.
 */
@customElement('ents-online-reservation')
export class OnlineReservation extends OnlineReservationBase {

  @property({type: Object, reflect: false})
  override config: OnlineReservationConfig = {
    articleDetailsUrl: '',
    messages: {},
    dataProtectionRegulationLink: '',
    reservationEndpoint: '',
    isNativeDialogEnabled: false
  };

  constructor() {
    super();
    this.messages = this.config.messages;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('ents:online-reservation:open', this.handleOpenEvent.bind(this));
    // kept for external consumers (e.g. Wishlist)
    document.addEventListener('ents:hva-modal:open', this.handleOpenEvent.bind(this));
    document.addEventListener('onlineReservationDrawer-drawer:close', this.handleCloseEvent.bind(this));
    this.messages = this.config.messages;
  }

  private handleOpenEvent(event: OpenOnlineReservationEvent) {
    // Check if we should use native dialog (same check as wishlist)
    if (this.shouldUseNativeDialog()) {
      this.openNativeBottomSheet(event.detail.articleId);
    } else {
      // Fall back to web drawer
      this.loadModel(event.detail.articleId);
      this.showDrawer();
    }
  }

  private shouldUseNativeDialog(): boolean {
    // Use server-side detection (AppInfoInterceptor determines this based on app version)
    return this.config.isNativeDialogEnabled === true;
  }

  private openNativeBottomSheet(variantId: string, attempts = 0) {
    const baseUrl = window.location.origin;
    const dialogUrl = `${baseUrl}/ents/online-reservation/app/layer/${variantId}/`;

    try {
      openNativeDialog({
        url: dialogUrl,
        height: 0.8,
        draggable: false,
        dismissible: true,
        externalUrlPaths: []
      });
    } catch (error) {
      console.error('Error opening native dialog', error);
      if (attempts < 10) {
        setTimeout(() => this.openNativeBottomSheet(variantId, attempts + 1), 200);
      } else {
        // Fall back to web drawer if native dialog fails
        this.loadModel(variantId);
        this.showDrawer();
      }
    }
  }

  private handleCloseEvent() {
    this.resetState();
  }

  render(): TemplateResult {
    return html`
      <ents-drawer id="onlineReservationDrawer" class="ents-or-content" full-screen close-aria-label="${this.messages.close}">
        ${this.renderContent()}
      </ents-drawer>
    `;
  }

  handleClose(): void {
    this.hideDrawer();
  }

  handleSuccessfulReservation(): void {
    this.appState = this.appState.setReservationStatus(true);
    track(this.getTrackingData());
    this.appState = this.appState.nextStep();
  }

  private showDrawer() {
    this.resetState();
    this.appState = this.appState.nextStep();
    return this.doOnDrawer(drawer => drawer.show());
  }

  private hideDrawer() {
    this.resetState();
    return this.doOnDrawer(drawer => drawer.hide());
  }

  private doOnDrawer(action: (drawer: EntsDrawer) => void) {
    const drawer = this.shadowRoot?.querySelector<EntsDrawer>('#onlineReservationDrawer');
    if (drawer) {
      action(drawer);
      return true;
    }
    return false;
  }
}
